import React, { createContext, useReducer, useCallback, useEffect } from 'react';

import userService from 'services/user';
import collection from 'services/collection';

import { usePriceContext } from './PriceContext';
import useWalletContext from 'hooks/useWalletContext';
import useTokenContext from 'hooks/useTokenContext';
import { formatResponse } from 'utils/collections';

const MAX_RECENT_COLLECTIONS = 5;

const initialState = {
  favoriteCollections: [],
  recentlyVisitedCollections: JSON.parse(
    localStorage.getItem('recentlyVisitedCollections') || '[]',
  ),
  loading: true,
  view: localStorage.getItem('collectionsView') || 'expanded',
};

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_FAVORITE_COLLECTIONS: 'SET_FAVORITE_COLLECTIONS',
  ADD_FAVORITE_COLLECTION: 'ADD_FAVORITE_COLLECTION',
  REMOVE_FAVORITE_COLLECTION: 'REMOVE_FAVORITE_COLLECTION',
  SET_VIEW: 'SET_VIEW',
  ADD_RECENTLY_VISITED_COLLECTION: 'ADD_RECENTLY_VISITED_COLLECTION',
};

const CollectionsReducer = (state, action = null) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case ACTIONS.SET_FAVORITE_COLLECTIONS:
      const rankedCollections = action.payload.map((item, index) => ({
        ...item,
        rank: index + 1,
      }));
      return {
        ...state,
        loading: false,
        favoriteCollections: rankedCollections,
      };
    case ACTIONS.ADD_FAVORITE_COLLECTION:
      const newFavorites = [...state.favoriteCollections, action.payload];
      newFavorites.sort((a, b) => a.rank - b.rank);
      const rerankedFavorites = newFavorites.map((item, index) => ({
        ...item,
        rank: index + 1,
      }));
      return {
        ...state,
        favoriteCollections: rerankedFavorites,
      };
    case ACTIONS.REMOVE_FAVORITE_COLLECTION:
      const filteredFavorites = state.favoriteCollections.filter(
        (item) => item.address !== action.payload,
      );
      const rerankedAfterRemoval = filteredFavorites.map((item, index) => ({
        ...item,
        rank: index + 1,
      }));
      return {
        ...state,
        favoriteCollections: rerankedAfterRemoval,
      };
    case ACTIONS.SET_VIEW:
      return {
        ...state,
        view: action.payload,
      };
    case ACTIONS.ADD_RECENTLY_VISITED_COLLECTION:
      const updatedCollections = [
        action.payload,
        ...state.recentlyVisitedCollections.filter(
          (item) => item.address !== action.payload.address,
        ),
      ].slice(0, MAX_RECENT_COLLECTIONS);

      localStorage.setItem('recentlyVisitedCollections', JSON.stringify(updatedCollections));

      return {
        ...state,
        recentlyVisitedCollections: updatedCollections,
      };
    default:
      throw Error('Unknown action: ' + action.type);
  }
};

export const CollectionsContext = createContext(null);

export const CollectionsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(CollectionsReducer, initialState);
  const { connectedAndAuth } = useTokenContext();
  const { address } = useWalletContext();
  const { coinPrice } = usePriceContext();

  const setLoading = (value) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: value });
  };

  const setFavoriteCollections = (collections) => {
    dispatch({ type: ACTIONS.SET_FAVORITE_COLLECTIONS, payload: collections });
  };

  const addFavoriteCollection = async (collection) => {
    dispatch({ type: ACTIONS.ADD_FAVORITE_COLLECTION, payload: collection });

    try {
      await userService.favoriteCollection({ tokenAddress: collection.address });
    } catch (error) {
      dispatch({ type: ACTIONS.REMOVE_FAVORITE_COLLECTION, payload: collection.address });
    }
  };

  const removeFavoriteCollection = async (address) => {
    dispatch({ type: ACTIONS.REMOVE_FAVORITE_COLLECTION, payload: address });

    try {
      await userService.unfavoriteCollection({ tokenAddress: address });
    } catch (error) {
      await getMyFavoriteCollections();
    }
  };

  const setView = (view) => {
    localStorage.setItem('collectionsView', view);
    dispatch({ type: ACTIONS.SET_VIEW, payload: view });
  };

  const getMyFavoriteCollections = useCallback(async () => {
    setLoading(true);
    const response = (await collection.getFavoriteCollections(address)) || { data: [] };
    const formattedResponse = formatResponse(response, coinPrice);
    setFavoriteCollections(formattedResponse);
    setLoading(false);
  }, [address, coinPrice]);

  const addRecentlyVisitedCollection = (collection) => {
    if (collection?.address) {
      dispatch({ type: ACTIONS.ADD_RECENTLY_VISITED_COLLECTION, payload: collection });
    }
  };

  useEffect(() => {
    if (!connectedAndAuth) {
      setFavoriteCollections([]);
      return;
    }

    getMyFavoriteCollections();
  }, [address, connectedAndAuth, getMyFavoriteCollections]);

  return (
    <CollectionsContext.Provider
      value={{
        state,
        dispatch,
        setLoading,
        addFavoriteCollection,
        removeFavoriteCollection,
        getMyFavoriteCollections,
        setView,
        addRecentlyVisitedCollection,
      }}
    >
      {children}
    </CollectionsContext.Provider>
  );
};
